/**
 * Scene solver — turns a soft `SceneSpec` (semantic intents: anchor, zones,
 * "near" targets, clusters) into exact geometry the renderer draws.
 *
 * Coordinate space: the sheet is SHEET_H units tall and aspect * SHEET_H wide.
 * The renderer's SVG viewBox is exactly that, so font sizes here are viewBox
 * px (a 36 unit font is 3.6% of the sheet height).
 *
 * Determinism + stability
 * -----------------------
 * Pure function of (spec, assets, prev). All randomness is seeded from element
 * ids. `prev` (a previous solve's `rects` map) is consulted first for every id:
 * if the old rect is still valid it is reused verbatim, so amending a spec
 * (more phrases, a new element) never shifts what is already on the page.
 *
 * Zones
 * -----
 * A zone name resolves, in order, to: an anchor region from the manifest
 * (peak / slope / base / canopy / roots / above / below …), then a built-in
 * (sky = above the anchor's ridge profile, left / right = margins beside the
 * anchor, ground = below it, anywhere). "near" resolves to `anchor.<region>`,
 * an element / cluster id, or `<zone>.<left|right|center|top|bottom>`.
 */

import { makeRng, seedFor, randRange } from '../lib/tapestryGenerative.js';
import { sceneArt } from '../lib/artPick.js';
import {
  Occupancy,
  polygonBBox,
  polygonCentroid,
  rectInsideness,
  rectsOverlap,
  inflate,
  segmentHitsRect,
  spiralCandidates,
  rectCenter,
  rectPolygon,
} from './geometry.js';
import { containerPath, containerPadding, connectorPath } from './hand.js';
import { solveWall, wallZones } from './wallSolver.js';
import { solveStory } from './storySolver.js';
import { templateFor } from './sceneTemplates.js';
import { LINE_GAP as TEXT_LINE_GAP, line, blockSink, oneIconPerSymbol } from './sceneText.js';

export const SHEET_H = 1000;
const DEFAULT_ASPECT = 16 / 9;
const MARGIN = 28;
// One sheet, every archetype (TT-1150): a live board is one board. What
// does not fit shrinks down the fit ladder or is skipped; the wall never
// tapes up more paper.

const SIZE_CLASS = { tiny: 0.09, small: 0.14, mid: 0.2, large: 0.3 };
const FONT = { title: 56, heading: 27, body: 25, small: 21 };
const WEIGHT_FONT = { 1: 22, 2: 25, 3: 29, 4: 34 };
const MIN_FONT_SCALE = 0.7; // never shrink a block's fonts below this of their tier (zone fit)
const HARD_MIN_FONT_SCALE = 0.5; // last resort when no spot exists at all
const LINE_GAP = 1.15;
const PHRASE_GAP = 10;
const ANCHOR_VALUE = 2; // occupancy value for the anchor silhouette (text may sit on it as a last resort)
const DENSE_VALUE = 3; // the anchor's solid paint: never under text (story boards)

const DEFAULT_PALETTE = { ink: '#2b2b2b', accent: '#e8562a', secondary: '#2a7ae8', paper: '#ffffff' };

// ---------------------------------------------------------------- helpers

function px(frac) {
  return frac * SHEET_H;
}

function fmt(n) {
  return Number(n.toFixed(1));
}

// Map image-normalized [u, v] into scene units given the full-image rect.
function imgToScene(rect, [u, v]) {
  return [rect.x + u * rect.w, rect.y + v * rect.h];
}

// ---------------------------------------------------------------- anchor

function placeAnchor(spec, assets, sheet) {
  const a = spec.anchor;
  // The anchor is the symbol's backdrop.
  const asset = a && sceneArt(assets, a.symbol_id, 'backdrop');
  if (!asset) return null;
  const [bx0, by0, bx1, by1] = asset.bbox;
  const paintedAspect = ((bx1 - bx0) * asset.width) / ((by1 - by0) * asset.height);
  const scale = a.scale ?? 1;
  // Painted width as a fraction of the sheet width.
  let pw = sheet.w * scale;
  let ph = pw / paintedAspect;
  if (ph > sheet.h * 0.95) {
    ph = sheet.h * 0.95;
    pw = ph * paintedAspect;
  }
  const placement = a.placement || 'center-low';
  let px0 = (sheet.w - pw) / 2;
  let py0 = sheet.h - ph;
  if (placement === 'center') py0 = (sheet.h - ph) / 2;
  else if (placement === 'left') px0 = MARGIN;
  else if (placement === 'right') px0 = sheet.w - pw - MARGIN;
  else if (placement === 'top') py0 = MARGIN;
  if (a.offset) {
    px0 += px(a.offset[0] || 0);
    py0 += px(a.offset[1] || 0);
  }
  const painted = { x: px0, y: py0, w: pw, h: ph };
  // Full-image rect (the painted bbox is a sub-rect of it).
  const full = {
    w: pw / (bx1 - bx0),
    h: ph / (by1 - by0),
  };
  full.x = painted.x - bx0 * full.w;
  full.y = painted.y - by0 * full.h;

  const regions = {};
  for (const [name, poly] of Object.entries(asset.regions || {})) {
    regions[name] = poly.map((p) => imgToScene(full, p));
  }
  return { id: 'anchor', asset, painted, full, regions, opacity: a.opacity ?? 1, z: 0 };
}

// Built-in zones derived from the anchor.
function builtinZones(anchor, sheet) {
  const sheetPoly = [
    [MARGIN, MARGIN],
    [sheet.w - MARGIN, MARGIN],
    [sheet.w - MARGIN, sheet.h - MARGIN],
    [MARGIN, sheet.h - MARGIN],
  ];
  const zones = { anywhere: sheetPoly };
  if (!anchor) {
    zones.sky = [
      [MARGIN, MARGIN],
      [sheet.w - MARGIN, MARGIN],
      [sheet.w - MARGIN, sheet.h * 0.45],
      [MARGIN, sheet.h * 0.45],
    ];
    zones.ground = [
      [MARGIN, sheet.h * 0.55],
      [sheet.w - MARGIN, sheet.h * 0.55],
      [sheet.w - MARGIN, sheet.h - MARGIN],
      [MARGIN, sheet.h - MARGIN],
    ];
    return zones;
  }
  const ridge = anchor.asset.ridge || [];
  const f = anchor.full;
  // Sky: everything above the ridge (clamped to the sheet).
  const sky = [[MARGIN, MARGIN]];
  if (ridge.length) {
    sky.push([MARGIN, Math.min(sheet.h - MARGIN, f.y + ridge[0] * f.h - 8)]);
    ridge.forEach((v, i) => {
      const u = (i + 0.5) / ridge.length;
      const x = f.x + u * f.w;
      if (x < MARGIN || x > sheet.w - MARGIN) return;
      sky.push([x, Math.max(MARGIN, Math.min(sheet.h - MARGIN, f.y + v * f.h - 8))]);
    });
    sky.push([sheet.w - MARGIN, Math.min(sheet.h - MARGIN, f.y + ridge[ridge.length - 1] * f.h - 8)]);
  } else {
    sky.push([MARGIN, anchor.painted.y], [sheet.w - MARGIN, anchor.painted.y]);
  }
  sky.push([sheet.w - MARGIN, MARGIN]);
  zones.sky = sky;
  const p = anchor.painted;
  const third = sheet.w * 0.34;
  zones.left = [
    [MARGIN, MARGIN],
    [third, MARGIN],
    [third, sheet.h - MARGIN],
    [MARGIN, sheet.h - MARGIN],
  ];
  zones.right = [
    [sheet.w - third, MARGIN],
    [sheet.w - MARGIN, MARGIN],
    [sheet.w - MARGIN, sheet.h - MARGIN],
    [sheet.w - third, sheet.h - MARGIN],
  ];
  if (p.y + p.h < sheet.h - MARGIN - 60) {
    zones.ground = [
      [MARGIN, p.y + p.h],
      [sheet.w - MARGIN, p.y + p.h],
      [sheet.w - MARGIN, sheet.h - MARGIN],
      [MARGIN, sheet.h - MARGIN],
    ];
  } else {
    // Anchor reaches the bottom: "ground" is its lower band.
    zones.ground = [
      [MARGIN, p.y + p.h * 0.7],
      [sheet.w - MARGIN, p.y + p.h * 0.7],
      [sheet.w - MARGIN, sheet.h - MARGIN],
      [MARGIN, sheet.h - MARGIN],
    ];
  }
  return zones;
}

// ---------------------------------------------------------------- targets

function subTarget(poly, part) {
  const b = polygonBBox(poly);
  switch (part) {
    case 'left':
      return [b.x + b.w * 0.2, b.y + b.h / 2];
    case 'right':
      return [b.x + b.w * 0.8, b.y + b.h / 2];
    case 'top':
      return [b.x + b.w / 2, b.y + b.h * 0.25];
    case 'bottom':
      return [b.x + b.w / 2, b.y + b.h * 0.75];
    default:
      return polygonCentroid(poly);
  }
}

function resolveNear(near, ctx) {
  if (!near) return null;
  const [head, part] = String(near).split('.');
  if (head === 'anchor' && ctx.anchor?.regions[part]) return polygonCentroid(ctx.anchor.regions[part]);
  if (ctx.rects[head]) {
    const r = ctx.rects[head];
    return part ? subTarget(rectPolygon(r), part) : rectCenter(r);
  }
  if (ctx.zones[head]) return subTarget(ctx.zones[head], part);
  return null;
}

function resolveZone(name, ctx) {
  if (!name) return ctx.zones.anywhere;
  return ctx.anchor?.regions[name] || ctx.zones[name] || ctx.zones.anywhere;
}

function isRegionZone(name, ctx) {
  return !!(name && ctx.anchor?.regions[name]);
}

// ---------------------------------------------------------------- search

/**
 * Find a free rect of size (w, h) inside `zone`, as close to `target` as
 * possible. Tries the previous rect first. Relaxes in stages: strict (no
 * overlap, fully inside) → allow sitting on the anchor art → allow partial
 * zone exit.
 */
// Relax gradually: first fully in-zone and off the art, then allow a little
// art under the block, then more, then mostly-out-of-zone as a last resort.
// anchorTol: fraction of a block allowed to sit on the anchor art;
// insideness: fraction of the block that must lie inside its zone;
// lineOk: the block may cross a drawn route (`ctx.route`) — the very last
// resort, after every line-free stage (TT-1229: a theme crossed by the
// trail reads worse than a moved one, but better than a dropped one; its
// paper covers the line under its letters).
const SEARCH_STAGES = [
  { anchorTol: 0, insideness: 0.9 },
  { anchorTol: 0, insideness: 0.6 },
  { anchorTol: 0.2, insideness: 0.8 },
  { anchorTol: 0.45, insideness: 0.6 },
  { anchorTol: 1, insideness: 0.6 },
  { anchorTol: 1, insideness: 0.35 },
  { anchorTol: 1, insideness: 0.15 },
  { anchorTol: 1, insideness: 0.15, lineOk: true },
];
const HOLD_STAGE = { anchorTol: 1, insideness: 0.5, lineOk: true }; // a grown block keeps its spot (a later segment never moves it)
const SEARCH_K = 12; // candidates scored per stage before taking the best
const SEARCH_MIN_STEP = 16;
const SEARCH_MIN_RADIUS = 600;
const SEARCH_PER_TURN = 14;
const SOFT_ART_BUSY = 0.15; // softArt: dense paint a block may cross (thin ridge / shadow lines)
const ART_COST = 1.6; // score weight of anchor wash under a block (vs. distance / zone diagonal)
const SOFT_ART_DISCOUNT = 0.7;

function findSpot({ id, w, h, zone, target, ctx, prev, pad = 12, onRegion = false, parts = null, softArt = false, radius = null, holdOnly = false }) {
  // softArt (art-first scenes): the anchor's pale wash never blocks, only
  // costs; its dense paint (DENSE_VALUE cells) still blocks like text past
  // a small fraction — `true` tolerates SOFT_ART_BUSY, a number sets the
  // fraction (a paper-backed tag may sit on the paint; text underneath
  // still blocks exactly).
  if (softArt) onRegion = true;
  const busyTol = softArt === true ? SOFT_ART_BUSY : softArt || 0;
  const { occ, placed } = ctx;
  const rng = makeRng(seedFor(id, 3));
  // `parts`: optional local sub-rects (relative to the block's top-left). When
  // given, occupancy and overlap are tested per part instead of on the whole
  // bounding box, so blocks with internal whitespace can interleave — the
  // density a graphic recorder gets by tucking one cluster into another's gaps.
  const footprint = (r) => (parts ? parts.map((q) => ({ x: r.x + q.x, y: r.y + q.y, w: q.w, h: q.h })) : [r]);
  // Stage-independent facts about a rect, computed once per candidate and
  // reused across the relaxation stages:
  //   blocked   — out of bounds, or a padded part sits on occupied cells
  //               (text / dense paint; the anchor wash is not "busy"); with
  //               softArt the grid is tolerant, so the exact overlap test
  //               against `placed` is the text guard there — otherwise the
  //               grid is a strict superset of that test and suffices
  //   inside    — fraction of the rect inside the zone
  //   anchorCov — the largest fraction of any padded part on the anchor wash
  // A padded part crossing a story's drawn route (`ctx.route`, TT-1229) is
  // tested last and only when a stage forbids it (all but the final one):
  // the line is a strong preference, not a block. A held block is not asked
  // (HOLD_STAGE allows it) — nothing inked moves under a later segment.
  const route = ctx.route;
  const crossesRoute = (r) => {
    const reach = pad + route.half;
    return footprint(r).some((part) => {
      const box = inflate(part, reach);
      return route.lines.some((pts) => pts.some((p, i) => i > 0 && segmentHitsRect(pts[i - 1], p, box)));
    });
  };
  const probe = (r) => {
    if (!inBounds(r, ctx)) return { blocked: 'bounds' };
    let anchorCov = 0;
    for (const part of footprint(r)) {
      const [busy, art] = occ.coverages(inflate(part, pad), ANCHOR_VALUE);
      if (busy > busyTol) return { blocked: 'occupied' };
      if (art > anchorCov) anchorCov = art;
      if (softArt && placed.some((p) => rectsOverlap(part, p, pad))) return { blocked: 'overlap' };
    }
    return { blocked: null, inside: rectInsideness(r, zone), anchorCov, r };
  };
  const onLine = (info) => {
    if (info.onLine == null) info.onLine = !!route && crossesRoute(info.r);
    return info.onLine;
  };
  // A zone that IS an anchor region (slope, base, canopy…) is meant to sit on
  // the art, so the anchor never counts there (onRegion).
  const fits = (info, { anchorTol, insideness, lineOk = false }, why) => {
    if (info.blocked) why[info.blocked]++;
    else if (info.inside < insideness) why.zone++;
    else if (!onRegion && anchorTol < 1 && info.anchorCov > anchorTol) why.anchor++;
    else if (!lineOk && onLine(info)) why.line++;
    else return true;
    return false;
  };
  const newWhy = () => ({ bounds: 0, zone: 0, occupied: 0, anchor: 0, overlap: 0, line: 0 });
  if (prev) {
    // Stability: same rect if it still fits; a block that grew keeps its
    // top-left corner (text extends down/right), else its center.
    //
    // The unchanged rect is judged geometrically (bounds + no overlap with
    // what is already placed), NOT on the occupancy grid: held neighbours
    // were placed pad-apart, but two pad-apart rects can share a grid cell,
    // so a grid test would fail a held rect depending on which neighbour
    // happened to be committed first. Art under it is unchanged since it
    // was first placed, so it needs no re-check either.
    if (prev.w === w && prev.h === h && inBounds(prev, ctx) && !footprint(prev).some((part) => placed.some((p) => rectsOverlap(part, p, 0)))) return prev;
    const why = newWhy();
    const corner = { x: prev.x, y: prev.y, w, h };
    if (fits(probe(corner), HOLD_STAGE, why)) return corner;
    const centered = { x: prev.x + (prev.w - w) / 2, y: prev.y + (prev.h - h) / 2, w, h };
    if (fits(probe(centered), HOLD_STAGE, why)) return centered;
  }
  // holdOnly: the caller is asking "can the previous placement absorb this
  // size?" and will try other sizes before it lets the block move at all.
  if (holdOnly) return null;

  const zb = polygonBBox(zone);
  const start = target || polygonCentroid(zone);
  const cands = spiralCandidates(start[0], start[1], {
    step: Math.max(SEARCH_MIN_STEP, Math.min(w, h) * 0.2),
    maxRadius: radius || Math.max(zb.w, zb.h, SEARCH_MIN_RADIUS),
    perTurn: SEARCH_PER_TURN,
    rng,
  });
  const diag = Math.hypot(zb.w, zb.h) || 1;
  const infos = new Array(cands.length);
  let why = null;
  for (const stage of SEARCH_STAGES) {
    why = newWhy(); // the record kept is the most relaxed stage's verdicts
    let best = null;
    let bestScore = Infinity;
    let found = 0;
    for (let i = 0; i < cands.length; i++) {
      const [cx, cy] = cands[i];
      const r = { x: cx - w / 2, y: cy - h / 2, w, h };
      const info = (infos[i] ??= probe(r));
      if (!fits(info, stage, why)) continue;
      if (info.art == null) info.art = softArt ? occ.coverageOf(r, ANCHOR_VALUE) * SOFT_ART_DISCOUNT : onRegion ? 0 : occ.coverageOf(r, ANCHOR_VALUE);
      const score = Math.hypot(cx - start[0], cy - start[1]) / diag + info.art * ART_COST;
      if (score < bestScore) {
        bestScore = score;
        best = r;
      }
      if (++found >= SEARCH_K) break;
    }
    if (best) return best;
  }
  // One skipped entry per id per ladder: consecutive failures for the same
  // block (its scale / shape rungs, then wider zones) collapse into the latest.
  const entry = { id, w: Math.round(w), h: Math.round(h), zone: polygonBBox(zone), start, why, tries: 1 };
  const last = ctx.skipped[ctx.skipped.length - 1];
  if (last && last.id === id) {
    entry.tries = last.tries + 1;
    ctx.skipped[ctx.skipped.length - 1] = entry;
  } else ctx.skipped.push(entry);
  return null;
}

/**
 * Hold pass for a block that has several candidate layouts (a scale / shape
 * ladder). Before any layout is allowed to move the block, ask whether the
 * previous placement can absorb one of them — the exact previous size first
 * (an unchanged block must come back verbatim), then any layout that fits at
 * the old corner / centre. `tryAt(L, holdOnly)` → rect | null.
 * Returns { r, L } or null; the caller then runs its normal ladder.
 */
function holdLadder(prev, layouts, tryAt) {
  if (!prev) return null;
  for (const L of layouts) {
    if (L && L.w === prev.w && L.h === prev.h) {
      const r = tryAt(L, true);
      if (r) return { r, L };
    }
  }
  for (const L of layouts) {
    const r = L && tryAt(L, true);
    if (r) return { r, L };
  }
  return null;
}

/**
 * Held-first order: items already on the page (a `prev` rect) are placed
 * before new arrivals, so a newcomer can never take the spot a held item is
 * about to reclaim. Stable, so the caller's order survives within each group.
 */
function heldFirst(items, prev, idOf = (it) => it.id) {
  return items.slice().sort((a, b) => (prev?.[idOf(a)] ? 0 : 1) - (prev?.[idOf(b)] ? 0 : 1));
}

/** A rect is in bounds when it sits inside the sheet's margins. */
function inBounds(r, ctx) {
  const { w, h } = ctx.sheet;
  return r.x >= MARGIN && r.y >= MARGIN && r.x + r.w <= w - MARGIN && r.y + r.h <= h - MARGIN;
}

/** Record a placed block: mark occupancy + overlap list, per part if given. */
function commitSpot(ctx, r, parts = null) {
  const rects = parts ? parts.map((q) => ({ x: r.x + q.x, y: r.y + q.y, w: q.w, h: q.h })) : [r];
  for (const q of rects) {
    ctx.placed.push(q);
    ctx.occ.markRect(q);
  }
}

/**
 * Place one block and commit it: the single entry point every archetype
 * uses. Looks up `prev[id]`, searches `zone` near `target` (findSpot); on
 * success records `ctx.rects[id]` and marks occupancy (per part if `parts`
 * is given).
 *
 * @param opts { id, w, h, target, zone, parts, pad, softArt, onRegion, radius, holdOnly }
 * @returns rect | null
 */
function placeBlock(ctx, prev, { id, w, h, target, zone, parts = null, pad = 12, softArt = false, onRegion = false, radius = null, holdOnly = false }) {
  const r = findSpot({ id, w, h, zone, target, ctx, prev: prev?.[id], pad, parts, softArt, onRegion, radius, holdOnly });
  if (!r) return null;
  ctx.rects[id] = r;
  commitSpot(ctx, r, parts);
  return r;
}

// ---------------------------------------------------------------- text

function measureBlock(phrase, fontSize, targetWidth, measure, maxLines = 4) {
  let fs = fontSize;
  let lay = layoutTextWith(phrase, fs, targetWidth, measure);
  while (lay && lay.lineCount > maxLines && fs > 18) {
    fs *= 0.9;
    lay = layoutTextWith(phrase, fs, targetWidth * 1.1, measure);
  }
  return { fontSize: fs, layout: lay };
}

// layoutText from the packer uses the global measureText (Hand1). We want the
// scene's font, so re-implement the greedy wrap with an injected measure.
function layoutTextWith(sentence, fontSize, targetWidth, measure) {
  const words = sentence.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return null;
  const ww = words.map((w, i) => measure(w + (i < words.length - 1 ? ' ' : ''), fontSize));
  const effective = Math.max(targetWidth, Math.max(...ww) * 1.05);
  const lines = [[]];
  const widths = [0];
  for (let i = 0; i < words.length; i++) {
    const last = widths.length - 1;
    if (widths[last] + ww[i] > effective && lines[last].length) {
      lines.push([]);
      widths.push(0);
    }
    lines[lines.length - 1].push(words[i]);
    widths[widths.length - 1] += ww[i];
  }
  const lh = fontSize * LINE_GAP;
  return {
    lines: lines.map((l) => l.join(' ')),
    lineCount: lines.length,
    maxLineWidth: Math.max(...widths),
    lineHeight: lh,
    totalHeight: lines.length * lh,
  };
}

// ---------------------------------------------------------------- solve

/**
 * Every placed item is keyed by its id: `ctx.rects[id]`, `prev[id]`, and the
 * rng seed (`seedFor` hashes a string). Coerce ids to strings and give the
 * id-less a positional one, so a numeric or missing id can neither seed a
 * constant rng nor land in `rects.undefined`. (Positional ids are only as
 * stable as the spec order — author ids for anything that must hold.)
 */
function withStringIds(spec) {
  const ids = (list, prefix) => (list || []).map((it, i) => (it && typeof it === 'object' ? { ...it, id: String(it.id ?? `${prefix}-${i}`) } : it));
  return {
    ...spec,
    ...(spec.elements ? { elements: ids(spec.elements, 'el') } : {}),
    ...(spec.clusters ? { clusters: ids(spec.clusters, 'cluster') } : {}),
    ...(spec.big_ideas ? { big_ideas: ids(spec.big_ideas, 'idea') } : {}),
    ...(spec.themes ? { themes: ids(spec.themes, 'theme') } : {}),
  };
}

/**
 * A spot icon draws its symbol's watercolour mid when the catalog has one
 * (TT-1337): `art`, the whole image's box in scene units, sized so its
 * painted bbox fits the icon's square, centred there. Without one the host
 * draws the symbol library's line icon, tinted, as before.
 */
function withIconArt(icons, assets) {
  return icons.map((icon) => {
    const asset = sceneArt(assets, icon.symbolId, 'mid');
    if (!asset) return icon;
    const [bx0, by0, bx1, by1] = asset.bbox;
    const k = Math.min(icon.w / ((bx1 - bx0) * asset.width), icon.h / ((by1 - by0) * asset.height));
    const w = asset.width * k;
    const h = asset.height * k;
    const x = icon.x + (icon.w - (bx1 - bx0) * w) / 2 - bx0 * w;
    const y = icon.y + (icon.h - (by1 - by0) * h) / 2 - by0 * h;
    return { ...icon, art: { url: asset.url, x, y, w, h } };
  });
}

/**
 * @param spec    SceneSpec (see fixtures/climb.json)
 * @param assets  symbol_id → role → art, from useSceneAssets (read with sceneArt)
 * @param opts    { measure(text, fontSize) → width in scene units, prev: rects map, fontFamily,
 *                  terrain: false to leave containers undressed (board/terrain.js) }
 */
export function solveScene(rawSpec, assets, { measure, prev = null, fontFamily, textBoxArt = {} } = {}) {
  const spec = oneIconPerSymbol(withStringIds(rawSpec));
  const aspect = spec.sheet?.aspect || DEFAULT_ASPECT;
  const sheet = { w: SHEET_H * aspect, h: SHEET_H };
  const basePalette = { ...DEFAULT_PALETTE, ...(spec.palette || {}) };
  const palette = basePalette;
  const occ = new Occupancy(sheet.w, sheet.h);

  // Word-wall archetype (Ink Factory register): no anchor image, title word
  // is the anchor, tree clusters with spot icons. See wallSolver.js.
  if (spec.archetype === 'wall') {
    const fonts = { caps: spec.fonts?.caps || fontFamily, script: spec.fonts?.script || fontFamily };
    const ctx = { sheet, margin: MARGIN, occ, anchor: null, zones: wallZones(sheet, MARGIN), rects: {}, placed: [], skipped: [] };
    const out = solveWall(spec, ctx, { measure, placeBlock, commitSpot, holdLadder, heldFirst, resolveNear, resolveZone, palette, fonts, prev });
    return {
      viewBox: { width: sheet.w, height: sheet.h },
      focus: out.focus,
      aspect,
      palette,
      fontFamily: fonts.caps,
      title: null,
      images: [],
      shapes: out.shapes,
      texts: out.texts,
      icons: withIconArt(out.icons, assets),
      rects: ctx.rects,
      debug: { zones: { ...ctx.zones }, regions: {}, rects: ctx.rects, skipped: ctx.skipped },
    };
  }
  // Story archetype: the wall's machinery (per-part footprints, the fit
  // ladder) plus an anchor image placed structurally. See storySolver.js.
  if (spec.archetype === 'story') {
    const fonts = { caps: spec.fonts?.caps || fontFamily, script: spec.fonts?.script || fontFamily };
    const wallOcc = occ;
    // Art-first: the template decides the anchor's size and placement; the
    // spec only names the metaphor. Pale wash is soft occupancy, dense paint hard.
    const T = templateFor(spec.metaphor);
    // No template anchor (the open board): no art, whatever the spec names.
    // The metaphor's template names its art: a spec carries the metaphor only.
    const anchorSpec = T.anchor || null;
    const anchor = placeAnchor({ ...spec, anchor: anchorSpec }, assets, sheet);
    if (anchor) {
      wallOcc.markAlpha(anchor.asset.alpha, anchor.full, { dilate: 0, value: ANCHOR_VALUE });
      if (anchor.asset.alpha.dense) wallOcc.markAlpha({ ...anchor.asset.alpha, data: anchor.asset.alpha.dense }, anchor.full, { dilate: 0, value: DENSE_VALUE });
    }
    // Zones are the wall's sheet bands, except `sky`, which follows the
    // anchor's ridge profile (the paint's actual top per column) — band art
    // like the river reaches the top edge with faint wash, so the image box
    // would leave no sky at all.
    const zones = { ...wallZones(sheet, MARGIN), ...(anchor ? { sky: builtinZones(anchor, sheet).sky } : {}) };
    const ctx = { sheet, margin: MARGIN, occ: wallOcc, anchor, assets, zones, rects: {}, placed: [], skipped: [] };
    // The art sets the colours: the template palette beats the spec's stock one.
    const palette = { ...basePalette, ...(T.palette || {}) };
    const out = solveStory(spec, ctx, { measure, placeBlock, commitSpot, holdLadder, palette, fonts, prev, T, textBoxArt });
    const images = [
      ...(anchor ? [{ id: 'anchor', url: anchor.asset.url, reveal: anchor.asset.reveal, x: anchor.full.x, y: anchor.full.y, w: anchor.full.w, h: anchor.full.h, z: 0, opacity: anchor.opacity }] : []),
      ...(out.images || []),
    ];
    return {
      viewBox: { width: sheet.w, height: sheet.h },
      focus: out.focus,
      aspect,
      palette,
      fontFamily: fonts.caps,
      title: null,
      images,
      shapes: out.shapes,
      texts: out.texts,
      icons: withIconArt(out.icons, assets),
      rects: ctx.rects,
      debug: { zones: { ...ctx.zones }, regions: anchor?.regions || {}, rects: ctx.rects, skipped: ctx.skipped, beats: out.beatPts, terrain: ctx.terrainOf || {} },
    };
  }
  const anchor = placeAnchor(spec, assets, sheet);
  if (anchor) occ.markAlpha(anchor.asset.alpha, anchor.full, { dilate: 1, value: ANCHOR_VALUE });

  const ctx = { sheet, margin: MARGIN, occ, anchor, zones: builtinZones(anchor, sheet), rects: {}, placed: [], skipped: [] };
  const images = [];
  const texts = [];
  const shapes = [];
  const debug = { zones: { ...ctx.zones }, regions: anchor?.regions || {}, rects: ctx.rects, skipped: ctx.skipped };

  // Title block reserves the top-left corner.
  let title = null;
  if (spec.title) {
    const fs = FONT.title;
    const w = Math.min(sheet.w * 0.5, measure(spec.title, fs) + 20);
    const r = { x: MARGIN + 10, y: MARGIN + 6, w, h: fs * 1.25 };
    title = { text: spec.title, x: r.x, y: r.y, fontSize: fs, color: palette.ink };
    occ.markRect(inflate(r, 10));
    ctx.placed.push(r);
    ctx.rects.title = r;
  }

  if (anchor) {
    images.push({
      id: 'anchor',
      url: anchor.asset.url,
      reveal: anchor.asset.reveal,
      x: anchor.full.x,
      y: anchor.full.y,
      w: anchor.full.w,
      h: anchor.full.h,
      z: 0,
      opacity: anchor.opacity,
    });
  }

  // Place one object element (image).
  const placeElement = (el) => {
    const asset = sceneArt(assets, el.symbol_id, 'mid');
    if (!asset) return;
    const [bx0, by0, bx1, by1] = asset.bbox;
    const paintedAspect = ((bx1 - bx0) * asset.width) / ((by1 - by0) * asset.height);
    const h = px(SIZE_CLASS[el.size] || SIZE_CLASS.mid);
    const w = h * paintedAspect;
    const zone = resolveZone(el.zone || asset.zone, ctx);
    const target = resolveNear(el.near, ctx);
    const r = placeBlock(ctx, prev, { id: el.id, w, h, zone, target, onRegion: isRegionZone(el.zone || asset.zone, ctx) });
    if (!r) return;
    // The image element is the full image scaled so its painted bbox = r.
    const fw = w / (bx1 - bx0);
    const fh = h / (by1 - by0);
    images.push({
      id: el.id,
      url: asset.url,
      reveal: asset.reveal,
      x: r.x - bx0 * fw,
      y: r.y - by0 * fh,
      w: fw,
      h: fh,
      z: 1,
      opacity: el.opacity ?? 1,
      sent: el.sent,
    });
  };

  // Measure one phrase cluster into a block, place it, emit container + guides.
  const placeCluster = (cl) => {
    const phrases = (cl.phrases || []).filter((p) => p && p.text);
    if (!phrases.length && !cl.label) return;
    const zone = resolveZone(cl.zone, ctx);
    const zb = polygonBBox(zone);
    const targetWidth = Math.max(300, Math.min(zb.w * (cl.width || 0.45), sheet.w * 0.23));
    const rng = makeRng(seedFor(cl.id, 11));

    // Measure every phrase (and the label) into a stacked block at a font
    // scale. Returns null if nothing measured.
    const measureAt = (fontScale) => {
      const blocks = [];
      let maxW = 0;
      let totalH = 0;
      if (cl.label) {
        const fs = FONT.heading * fontScale;
        const label = cl.label.toUpperCase();
        const w = measure(label, fs);
        blocks.push({
          kind: 'label',
          text: label,
          fontSize: fs,
          lines: [label],
          lineHeight: fs * LINE_GAP,
          w,
          h: fs * LINE_GAP,
          color: palette.accent,
        });
        maxW = Math.max(maxW, w);
        totalH += fs * LINE_GAP + PHRASE_GAP * 0.6;
      }
      for (const p of phrases) {
        const fs = (WEIGHT_FONT[p.weight] || FONT.body) * fontScale;
        const m = measureBlock(p.text, fs, targetWidth * fontScale, measure);
        if (!m.layout) return;
        const w = m.layout.maxLineWidth;
        blocks.push({
          kind: 'phrase',
          text: p.text,
          sent: p.sent,
          fontSize: m.fontSize,
          lines: m.layout.lines,
          lineHeight: m.layout.lineHeight,
          w,
          h: m.layout.totalHeight,
          color: palette.ink,
          keywords: p.keywords || [],
        });
        maxW = Math.max(maxW, w);
        totalH += m.layout.totalHeight + PHRASE_GAP;
      }
      totalH -= PHRASE_GAP;
      if (!blocks.length) return null;
      const pad = containerPadding(cl.container, FONT.body * fontScale);
      return { blocks, maxW, totalH, pad, w: maxW + pad * 2, h: totalH + pad * 2, fontScale };
    };

    // Shrink-to-fit against the zone's bbox (to a floor), then place. If no
    // spot exists even so, shrink harder, and finally fall back to anywhere.
    let fontScale = 1;
    let m = measureAt(fontScale);
    if (!m) return;
    for (let attempt = 0; attempt < 4; attempt++) {
      const fitW = (zb.w * 0.92) / Math.max(1, m.w);
      const fitH = (zb.h * 0.85) / Math.max(1, m.h);
      const need = Math.min(fitW, fitH);
      if (need >= 1 || fontScale <= MIN_FONT_SCALE) break;
      fontScale = Math.max(MIN_FONT_SCALE, fontScale * Math.max(0.6, need));
      m = measureAt(fontScale);
    }
    const target = resolveNear(cl.near, ctx);
    const onRegion = isRegionZone(cl.zone, ctx);
    const place = (mm, z) => placeBlock(ctx, prev, { id: cl.id, w: mm.w, h: mm.h, zone: z, target, pad: 16, onRegion });
    let r = place(m, zone);
    if (!r) {
      m = measureAt(HARD_MIN_FONT_SCALE);
      r = m && place(m, zone);
    }
    if (!r && zone !== ctx.zones.anywhere) {
      r = m && place(m, ctx.zones.anywhere);
    }
    if (!r) return;
    const { blocks, pad } = m;
    const B = blockSink({ texts, shapes, icons: [] }, cl.id);

    if (cl.container && cl.container !== 'none') {
      const tail = cl.container === 'bubble' ? target : null;
      const d = containerPath(cl.container, r, rng, { tail });
      if (d)
        B.shape({
          id: `${cl.id}-container`,
          kind: 'container',
          d,
          stroke: palette.ink,
          fill: palette.paper,
          fillOpacity: 0.55,
          strokeWidth: 3,
          sent: cl.sent ?? phrases[0]?.sent,
          z: 2,
        });
    }

    // Emit one text entry per block (every line at the block's width).
    let y = r.y + pad;
    const center = (cl.align || 'left') === 'center';
    for (let bi = 0; bi < blocks.length; bi++) {
      const b = blocks[bi];
      const lay = { lines: b.lines, widths: b.lines.map(() => b.w) };
      B.text(`${cl.id}-${bi}`, lay, center ? r.x + r.w / 2 : r.x + pad, y, b.fontSize, fontFamily, b.color, {
        sent: b.sent,
        tilt: randRange(rng, -0.012, 0.012),
        uppercase: b.kind === 'label',
        center,
        lineHeight: b.lineHeight,
      });
      y += b.h + (b.kind === 'label' ? PHRASE_GAP * 0.6 : PHRASE_GAP);
    }
  };

  // Placement order: anything an item is "near" goes first (so the target
  // exists); among ready items, text clusters go before decorative objects —
  // the phrases are the content, the objects decorate around them.
  {
    const items = [
      ...(spec.elements || []).map((it) => ({ kind: 'el', it })),
      ...(spec.clusters || []).map((it) => ({ kind: 'cl', it })),
    ];
    const ids = new Set(items.map((i) => i.it.id));
    const depOf = (it) => {
      const head = it.near ? String(it.near).split('.')[0] : null;
      return head && ids.has(head) ? head : null;
    };
    const done = new Set();
    let remaining = items.slice();
    while (remaining.length) {
      const ready = remaining.filter((i) => {
        const d = depOf(i.it);
        return !d || done.has(d) || d === i.it.id;
      });
      // Anything already on the page from the previous solve is re-placed first, so
      // new arrivals never take a spot an existing item was holding (stability).
      const held = remaining.find((i) => prev && prev[i.it.id]);
      const pick = held || ready.find((i) => i.kind === 'cl') || ready[0] || remaining[0];
      if (pick.kind === 'el') placeElement(pick.it);
      else placeCluster(pick.it);
      done.add(pick.it.id);
      remaining = remaining.filter((i) => i !== pick);
    }
  }

  // Connectors between placed rects.
  for (const c of spec.connectors || []) {
    const from = ctx.rects[c.from];
    const to = ctx.rects[c.to];
    if (!from || !to) continue;
    const rng = makeRng(seedFor(`${c.from}->${c.to}`, 5));
    const { d, head, mid } = connectorPath(from, to, rng);
    const B = blockSink({ texts, shapes, icons: [] }, `conn-${c.from}-${c.to}`);
    B.shape({
      id: `conn-${c.from}-${c.to}`,
      kind: 'connector',
      d,
      stroke: c.color === 'accent' ? palette.accent : palette.secondary,
      fill: 'none',
      strokeWidth: 4,
      sent: c.sent,
      z: 2,
    });
    B.shape({
      id: `conn-${c.from}-${c.to}-head`,
      kind: 'connector',
      d: head,
      stroke: c.color === 'accent' ? palette.accent : palette.secondary,
      fill: 'none',
      strokeWidth: 4,
      sent: c.sent,
      z: 2,
    });
    if (c.label) {
      const fs = FONT.small;
      const w = measure(c.label, fs);
      // Label sits just off the arc's control point, on the outer side.
      const fc = [from.x + from.w / 2, from.y + from.h / 2];
      const tc = [to.x + to.w / 2, to.y + to.h / 2];
      const mx = (fc[0] + tc[0]) / 2;
      const my = (fc[1] + tc[1]) / 2;
      const ox = mid[0] - mx;
      const oy = mid[1] - my;
      const ol = Math.hypot(ox, oy) || 1;
      const lx = mid[0] + (ox / ol) * 10 - w / 2;
      const ly = mid[1] + (oy / ol) * 10 + fs * 0.35;
      // `ly` is the baseline; the sink offsets from the line top, so fold that out
      B.text(`conn-${c.from}-${c.to}-label`, line(c.label, fs, measure), lx, ly - fs * TEXT_LINE_GAP * 0.8, fs, fontFamily, c.color === 'accent' ? palette.accent : palette.secondary, { sent: c.sent });
    }
  }

  return {
    viewBox: { width: sheet.w, height: sheet.h },
    aspect,
    palette,
    fontFamily,
    title,
    images: images.sort((a, b) => a.z - b.z),
    shapes,
    texts,
    rects: { ...ctx.rects },
    debug,
  };
}

/**
 * Restrict a spec to what has been "heard" so far: elements, phrases,
 * connectors with `sent <= currentSent` (items without `sent` always show).
 */
export function specAtSentence(spec, currentSent) {
  if (currentSent == null || currentSent === Infinity) return spec;
  const ok = (x) => x.sent == null || x.sent <= currentSent;
  return {
    ...spec,
    elements: (spec.elements || []).filter(ok),
    clusters: (spec.clusters || [])
      .map((c) => ({
        ...c,
        phrases: (c.phrases || []).filter(ok),
        children: (c.children || [])
          .filter(ok)
          .map((ch) => ({ ...ch, fragments: (ch.fragments || []).filter((f) => typeof f === 'string' || ok(f)) })),
        quotes: (c.quotes || []).filter(ok),
      }))
      .filter((c) => c.phrases.length || c.children.length || c.quotes.length || (c.sent != null && c.sent <= currentSent)),
    connectors: (spec.connectors || []).filter(ok),
    // Story fields (absent on other archetypes).
    ...(spec.arc ? { arc: spec.arc.filter(ok) } : {}),
    ...(spec.big_ideas ? { big_ideas: spec.big_ideas.filter(ok) } : {}),
    ...(spec.tensions ? { tensions: spec.tensions.filter(ok) } : {}),
    ...(spec.decisions ? { decisions: spec.decisions.filter(ok) } : {}),
    ...(spec.themes
      ? {
          themes: spec.themes
            .map((c) => ({ ...c, children: (c.children || []).filter(ok).map((ch) => ({ ...ch, fragments: (ch.fragments || []).filter(ok) })) }))
            .filter((c) => c.children.length || (c.sent != null && c.sent <= currentSent)),
        }
      : {}),
  };
}

export function specMaxSentence(spec) {
  let m = 0;
  const bump = (x) => {
    if (x && x.sent != null && x.sent > m) m = x.sent;
  };
  (spec.elements || []).forEach(bump);
  (spec.clusters || []).forEach((c) => {
    bump(c);
    (c.phrases || []).forEach(bump);
    (c.children || []).forEach((ch) => {
      bump(ch);
      (ch.fragments || []).forEach((f) => typeof f === 'object' && bump(f));
    });
    (c.quotes || []).forEach(bump);
  });
  (spec.connectors || []).forEach(bump);
  (spec.arc || []).forEach(bump);
  (spec.big_ideas || []).forEach(bump);
  (spec.tensions || []).forEach(bump);
  (spec.decisions || []).forEach(bump);
  (spec.themes || []).forEach((c) => {
    bump(c);
    (c.children || []).forEach((ch) => {
      bump(ch);
      (ch.fragments || []).forEach((f) => typeof f === 'object' && bump(f));
    });
  });
  return m;
}
